import { useMemo, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Warehouse } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  type DataTableColumn,
} from "@bizop/ui-kit";
import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  mockBranches,
  mockWarehouses,
  type MockBranch,
  type MockWarehouse,
  type WarehouseType,
} from "../../../lib/inventoryMock";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";

export const Route = createFileRoute("/_app/inventory/warehouses")({
  component: WarehousesPage,
});

/* ─────────────────────────────────────────────────────────────
 * Types — local to this route
 * ───────────────────────────────────────────────────────────── */

type WarehouseRow = {
  _id: string;
  branchId: string;
  code: string;
  displayName: string;
  warehouseType: WarehouseType;
  isActive: boolean;
};

type BranchRow = {
  _id: string;
  displayName: string;
};

const WAREHOUSE_TYPE_LABELS: Record<WarehouseType, string> = {
  raw_material: "Raw Material",
  finished_goods: "Finished Goods",
  transit: "Transit",
  dealer_consignment: "Dealer Consignment",
};

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function WarehousesPage() {
  const isMock = useMockData();

  // Local state — in mock mode we keep CRUD edits client-side so the page
  // is interactive without a backend.
  const warehousesSeed = useMemo(
    () =>
      (mockWarehouses as readonly MockWarehouse[]).map((w) => ({
        _id: w._id,
        branchId: w.branchId,
        code: w.code,
        displayName: w.displayName,
        warehouseType: w.warehouseType,
        isActive: w.isActive,
      })),
    [],
  );
  const [warehouses, setWarehouses] = usePersistentMockState<WarehouseRow[]>("warehouses", warehousesSeed);
  const [branchesState] = usePersistentMockState<MockBranch[]>(
    "branches",
    mockBranches,
  );
  const branches: BranchRow[] = useMemo(
    () =>
      isMock
        ? branchesState.map((b) => ({ _id: b._id, displayName: b.displayName }))
        : [],
    [isMock, branchesState],
  );
  const branchById = useMemo(
    () => new Map(branches.map((b) => [b._id, b])),
    [branches],
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<WarehouseRow | null>(null);
  const [detailRow, setDetailRow] = useState<WarehouseRow | null>(null);

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(row: WarehouseRow) {
    setEditing(row);
    setDetailRow(null);
    setSheetOpen(true);
  }

  function handleSubmit(values: Omit<WarehouseRow, "_id"> & { _id?: string }) {
    if (values._id) {
      setWarehouses((prev) =>
        prev.map((w) =>
          w._id === values._id ? { ...w, ...values, _id: w._id } : w,
        ),
      );
      toast.success("Warehouse updated. Audit trail recorded.");
    } else {
      setWarehouses((prev) => [
        ...prev,
        { ...values, _id: `wh_${Date.now()}` },
      ]);
      toast.success("Warehouse created (mock)");
    }
    setSheetOpen(false);
  }

  const columns = useMemo<DataTableColumn<WarehouseRow>[]>(
    () => [
      {
        id: "code",
        accessorKey: "code",
        header: "Code",
        meta: { mono: true },
        cell: (info) => (
          <span className="font-mono text-(--text-primary)">
            {String(info.getValue())}
          </span>
        ),
      },
      {
        id: "displayName",
        accessorKey: "displayName",
        header: "Name",
        cell: (info) => (
          <span className="text-(--text-primary)">
            {info.row.original.displayName}
          </span>
        ),
      },
      {
        id: "branch",
        header: "Branch",
        cell: (info) => (
          <span className="text-(--text-secondary)">
            {branchById.get(info.row.original.branchId)?.displayName ?? "—"}
          </span>
        ),
      },
      {
        id: "warehouseType",
        header: "Type",
        cell: (info) => (
          <Badge variant="neutral" size="sm">
            {WAREHOUSE_TYPE_LABELS[info.row.original.warehouseType]}
          </Badge>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        cell: (info) => (
          <Badge
            variant={info.row.original.isActive ? "success" : "neutral"}
            size="sm"
          >
            {info.row.original.isActive ? "Active" : "Archived"}
          </Badge>
        ),
      },
    ],
    [branchById],
  );

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<WarehouseRow>[] = [
      { key: "code", label: "Code" },
      { key: "displayName", label: "Name" },
      {
        key: "branchId",
        label: "Branch",
        value: (r) => branchById.get(r.branchId)?.displayName ?? r.branchId,
      },
      {
        key: "warehouseType",
        label: "Type",
        format: (v) => WAREHOUSE_TYPE_LABELS[v as WarehouseType] ?? String(v),
      },
      { key: "isActive", label: "Active" },
    ];
    downloadCsv(
      exportFilename("warehouses", extension),
      toCsv(warehouses, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Warehouses"
        description="Storage locations grouped by branch. Used as the cell key for every stock movement."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={openCreate}>
              <Plus className="size-4" />
              New warehouse
            </Button>
          </>
        }
      />

      <DataTable
        data={warehouses}
        columns={columns}
        emptyTitle="No warehouses yet"
        emptyDescription="Create your first warehouse to start tracking stock."
        emptyIcon={<Warehouse className="size-5 text-(--text-tertiary)" />}
        onRowClick={(row) => setDetailRow(row.original)}
      />

      {/* Detail Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) setDetailRow(null);
        }}
      >
        <SheetContent size="md">
          {detailRow ? (
            <WarehouseDetail
              warehouse={detailRow}
              branchName={
                branchById.get(detailRow.branchId)?.displayName ?? "—"
              }
              onEdit={() => openEdit(detailRow)}
              onClose={() => setDetailRow(null)}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>
              {editing ? "Edit warehouse" : "New warehouse"}
            </SheetTitle>
            <SheetDescription>
              {editing
                ? "Update the warehouse details. Code must remain unique."
                : "Codes are surfaced everywhere stock is referenced — keep them short and stable."}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <WarehouseEditForm
              key={editing?._id ?? "new"}
              branches={branches}
              initialValue={editing}
              onSubmit={handleSubmit}
              onCancel={() => setSheetOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Warehouse detail Sheet body
 * ───────────────────────────────────────────────────────────── */

function WarehouseDetail({
  warehouse,
  branchName,
  onEdit,
  onClose,
}: {
  warehouse: WarehouseRow;
  branchName: string;
  onEdit: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{warehouse.code}</span>
          <Badge
            variant={warehouse.isActive ? "success" : "neutral"}
            size="sm"
          >
            {warehouse.isActive ? "Active" : "Archived"}
          </Badge>
          <RecordAuditBadge tableName="warehouses" recordId={warehouse._id} />
        </SheetTitle>
        <SheetDescription>{warehouse.displayName}</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
          <section className="grid grid-cols-2 gap-3">
            <DetailField label="Code" value={warehouse.code} />
            <DetailField
              label="Type"
              value={WAREHOUSE_TYPE_LABELS[warehouse.warehouseType]}
            />
            <DetailField label="Branch" value={branchName} />
            <DetailField
              label="Status"
              value={warehouse.isActive ? "Active" : "Archived"}
            />
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Current stock summary
            </div>
            <div className="rounded-[var(--radius-md)] border border-dashed border-(--border-subtle) p-4 text-center text-[12px] text-(--text-tertiary)">
              Stock-by-warehouse summary available once backend aggregations
              are wired.
            </div>
          </section>
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail
              tableName="warehouses"
              recordId={warehouse._id}
            />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button size="icon" onClick={onEdit} aria-label="Edit">
          <Pencil className="size-4" />
        </Button>
      </SheetFooter>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary)">{value}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Form
 * ───────────────────────────────────────────────────────────── */

function WarehouseEditForm({
  branches,
  initialValue,
  onSubmit,
  onCancel,
}: {
  branches: BranchRow[];
  initialValue: WarehouseRow | null;
  onSubmit: (values: Omit<WarehouseRow, "_id"> & { _id?: string }) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initialValue?.code ?? "");
  const [displayName, setDisplayName] = useState(
    initialValue?.displayName ?? "",
  );
  const [branchId, setBranchId] = useState(
    initialValue?.branchId ?? branches[0]?._id ?? "",
  );
  const [warehouseType, setWarehouseType] = useState<WarehouseType>(
    initialValue?.warehouseType ?? "finished_goods",
  );
  const [isActive, setIsActive] = useState(initialValue?.isActive ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!code.trim()) errs.code = "Code is required.";
    if (!displayName.trim()) errs.displayName = "Name is required.";
    if (!branchId) errs.branchId = "Branch is required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    onSubmit({
      _id: initialValue?._id,
      code: code.trim().toUpperCase(),
      displayName: displayName.trim(),
      branchId,
      warehouseType,
      isActive,
    });
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormItem>
        <FormLabel required>Code</FormLabel>
        <FormControl>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. WH-HQ-FG"
            invalid={Boolean(errors.code)}
          />
        </FormControl>
        <FormMessage>{errors.code}</FormMessage>
      </FormItem>

      <FormItem>
        <FormLabel required>Display name</FormLabel>
        <FormControl>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. HQ Finished Goods"
            invalid={Boolean(errors.displayName)}
          />
        </FormControl>
        <FormMessage>{errors.displayName}</FormMessage>
      </FormItem>

      <FormItem>
        <FormLabel required>Branch</FormLabel>
        <FormControl>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger invalid={Boolean(errors.branchId)}>
              <SelectValue placeholder="Select branch" />
            </SelectTrigger>
            <SelectContent>
              {branches.map((b) => (
                <SelectItem key={b._id} value={b._id}>
                  {b.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormControl>
        <FormMessage>{errors.branchId}</FormMessage>
      </FormItem>

      <FormItem>
        <FormLabel required>Type</FormLabel>
        <FormControl>
          <Select
            value={warehouseType}
            onValueChange={(value) =>
              setWarehouseType(value as WarehouseType)
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(WAREHOUSE_TYPE_LABELS) as WarehouseType[]).map(
                (t) => (
                  <SelectItem key={t} value={t}>
                    {WAREHOUSE_TYPE_LABELS[t]}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </FormControl>
      </FormItem>

      <FormItem>
        <div className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3">
          <div>
            <div className="text-[13px] font-medium text-(--text-primary)">
              Active
            </div>
            <div className="text-[12px] text-(--text-tertiary)">
              Inactive warehouses cannot be selected on new movements.
            </div>
          </div>
          <Switch checked={isActive} onCheckedChange={setIsActive} />
        </div>
      </FormItem>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {initialValue ? "Save changes" : "Create warehouse"}
        </Button>
      </div>
    </Form>
  );
}
